import { useEffect, useMemo, useRef, useState } from "react";
import RolePicker from "./RolePicker";
import { useForm } from "react-hook-form";
import { useSearchParams, useNavigate } from "react-router-dom";

export default function FindSearch({
  locations,
  industry_tags,
  sponsored_roles,
  plan,
}) {
  const [searchParams] = useSearchParams();
  const [openDropdown, setOpenDropdown] = useState(null);
  const industryRef = useRef(null);
  const locationRef = useRef(null);
  const toggleDropdown = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  // const defaultValues = Object.fromEntries([...searchParams.entries()]);
  const defaultValues = {
    industry: searchParams.getAll("industry"),
    location: searchParams.getAll("location"),
    searchKeyword: searchParams.get("searchKeyword") || "",
    soc: searchParams.get("soc") || "",
    sponsor_activity: searchParams.get("sponsor_activity") || "",
    sponsor_rating: searchParams.get("sponsor_rating") || "",
    sortBy: searchParams.get("sortBy") || "",
    accept_overseas: searchParams.get("accept_overseas") === "true",
  };

  const {
    register,
    formState: { errors },
    setValue,
    watch,
    reset,
    handleSubmit,
  } = useForm({ defaultValues });

  // soc is driven by the picker, not by an <input>, so it is registered
  // manually and written with setValue.
  useEffect(() => {
    register("soc");
  }, [register]);

  const roleOptions = useMemo(
    () =>
      (sponsored_roles || []).map((r) => ({
        soc_code: String(r.soc_code),
        soc_name: r.soc_name,
        employers: r.employers,
        label: r.soc_name,
        search: String(r.soc_name || "").toLowerCase(),
      })),
    [sponsored_roles],
  );

  const navigate = useNavigate();
  const isFirstLoad = useRef(true);
  useEffect(() => {
    if (isFirstLoad.current && (locations?.length || industry_tags?.length)) {
      reset(defaultValues); // only reset once
      isFirstLoad.current = false;
    }
  }, [defaultValues, reset, locations, industry_tags]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        industryRef.current &&
        !industryRef.current.contains(e.target) &&
        locationRef.current &&
        !locationRef.current.contains(e.target)
      ) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const hasFilters = [...searchParams.entries()].length > 0;
    const isAllowedPlan = plan && [5, 6, 7, 8].includes(Number(plan.id));

    if (hasFilters && !isAllowedPlan) {
      navigate("/subscription", { replace: true });
    }
  }, [searchParams, plan, navigate]);

  const handleSearchCompanies = (data) => {
    const isAllowedPlan = plan && [5, 6, 7, 8].includes(Number(plan.id));
    if (!isAllowedPlan) {
      navigate("/subscription");
      return;
    }

    const params = new URLSearchParams();
    // Object.entries(data).forEach(([key, value]) => {
    //   if (value !== "" && value !== false) {
    //     params.append(key, value);
    //   }
    // });
    Object.entries(data).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, v));
      } else if (value !== "" && value !== false) {
        params.append(key, value);
      }
    });
    navigate(`/explore-companies/1?${params.toString()}`);
  };

  const handleClearFilters = () => {
    reset({
      industry: [],
      location: [],
      searchKeyword: "",
      soc: "",
      sponsor_activity: "",
      sponsor_rating: "",
      sortBy: "",
      accept_overseas: false,
    });

    navigate("/explore-companies/1");
  };

  return (
    <>
      <form method="post" onSubmit={handleSubmit(handleSearchCompanies)}>
        <div className="find_search">
          <p className="search_label">Search by company or profession</p>
          <div className="row form_row">
            <div className="col-md-6">
              <div className="form_blk flex_blk">
                <div className="relative flex-grow-1 flex_blk_in">
                  <img src="/images/icon_search.png" alt="" />
                  <input
                    type="text"
                    name="searchKeyword"
                    id="searchKeyword"
                    className="input"
                    placeholder="Search by company name"
                    {...register("searchKeyword")}
                  />
                </div>
                {/* <button type="submit" className="site_btn">
									Search
									<img src="/images/icon_arrow_right.png" alt="" />
								</button> */}
              </div>
            </div>

            <div className="col-md-6 role_picker_col">
              <RolePicker
                options={roleOptions}
                value={watch("soc")}
                onChange={(code) => setValue("soc", code)}
              />
            </div>

            <div className="col-md-3">
              <select
                name="sponsor_activity"
                id="sponsor_activity"
                className="input"
                {...register("sponsor_activity")}
              >
                <option value="">Sponsor Activity</option>
                <option value="active_sponsor">Active Sponsor</option>
                <option value="occasional_sponsor">Occasional Sponsor</option>
                <option value="inactive_sponsor">Inactive Sponsor</option>
              </select>
            </div>
            <div className="col-md-3">
              <select
                name="sponsor_rating"
                id="sponsor_rating"
                className="input"
                {...register("sponsor_rating")}
              >
                <option value="">Sponsor Rating</option>
                <option value="a_rated">A-Rated</option>
                <option value="b_rated">B-Rated</option>
                <option value="provisional">Provisional</option>
              </select>
            </div>
            <div className="col-md-3">
              {/* <select
                name="industry"
                id="industry"
                className="input"
                {...register("industry")}
              >
                <option value="">Industries</option>
                {industry_tags?.map((industry_tag) => (
                  <option value={industry_tag} key={industry_tag}>
                    {industry_tag}
                  </option>
                ))}
              </select> */}
              <div className="checkbox_group drop_down" ref={industryRef}>
                <button
                  type="button"
                  className="input drop_btn d-flex"
                  onClick={() => toggleDropdown("industry")}
                >
                  Industries <i className="chevron ms-auto"></i>
                </button>
                {openDropdown === "industry" && (
                  <ul className="drop_cnt drop_lst active">
                    {industry_tags?.map((industry) => (
                      <li key={industry}>
                        <div className="lbl_btn">
                          <input
                            type="checkbox"
                            id={industry}
                            value={industry}
                            {...register("industry")}
                            defaultChecked={defaultValues.industry?.includes(
                              industry,
                            )}
                          />
                          <label className="checkbox_lbl" htmlFor={industry}>
                            {industry}
                          </label>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="col-md-3" ref={locationRef}>
              <div className="checkbox_group drop_down">
                <button
                  type="button"
                  className="input drop_btn d-flex"
                  onClick={() => toggleDropdown("location")}
                >
                  Locations <i className="chevron ms-auto"></i>
                </button>
                {openDropdown === "location" && (
                  <ul className="drop_cnt drop_lst active">
                    {locations?.map((loc) => (
                      <li key={loc}>
                        <div className="lbl_btn">
                          <input
                            type="checkbox"
                            id={loc}
                            value={loc}
                            {...register("location")}
                            defaultChecked={defaultValues.location?.includes(
                              loc,
                            )}
                          />
                          <label htmlFor={loc} className="checkbox_lbl">
                            {loc}
                          </label>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* <select
                name="location"
                id="location"
                className="input"
                {...register("location")}
              >
                <option value="">Locations</option>
                {locations?.map((loc) => (
                  <option value={loc} key={loc}>
                    {loc}
                  </option>
                ))}
              </select> */}
            </div>

            <div className="col-md-3">
              <select
                name="sortBy"
                id="sortBy"
                className="input"
                {...register("sortBy")}
              >
                <option value="">Sort by</option>
                <option value="desc">Most Recent</option>
                <option value="asc">Least Recent</option>
              </select>
            </div>

            {/* <div className="col-md-6">
              <select name="" id="" className="input" {...register("sortBy")}>
                <option value="desc">Most Recent</option>
                <option value="asc">Least Recent</option>
              </select>
            </div> */}

            <div className="col-md-9 filter_actions">
              <div className="form_blk lbl_btn m-0">
                <input
                  type="checkbox"
                  name="accept_overseas"
                  id="accept_overseas"
                  {...register("accept_overseas")}
                />
                <label htmlFor="accept_overseas">
                  Accepts Overseas Applicants
                </label>
              </div>
              <div className="btn_blk">
                <button type="submit" className="site_btn">
                  Search
                  <img src="/images/icon_arrow_right.png" alt="" />
                </button>
                <button
                  type="button"
                  className="site_btn text border-0 ms-auto"
                  onClick={() => handleClearFilters()}
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}
